import { Temporal } from "@js-temporal/polyfill";
import { scheduleV1, type LocalPoint, type ScheduleV1 } from "@shared/schemas/schedule";
import { legacySchedule, parseScheduleJson, resolveSchedule, type LegacyScheduleRecord } from "@shared/validation/schedule";

export function newSchedule(timezone: string, date: string | null = null): ScheduleV1 {
  return { version: 1, timezone, eventStart: { date, time: null }, eventEnd: { date, time: null }, accessStart: { mode: "unknown" }, accessEnd: { mode: "unknown" }, reservation: { mode: "timed" } };
}
/** This is a form draft, never a repair on read. Unrelated saves omit schedule. */
export function scheduleDraft(value: unknown, legacy: LegacyScheduleRecord, timezone: string): ScheduleV1 {
  const parsed = scheduleV1.safeParse(parseScheduleJson(value));
  return parsed.success ? parsed.data : legacySchedule(legacy, timezone);
}
export function durationLabel(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes < 0) return "To be confirmed";
  if (minutes === 0) return "0 min";
  const hours = Math.floor(minutes / 60), rest = Math.round((minutes % 60) * 100) / 100;
  return [hours ? `${hours} hr${hours === 1 ? "" : "s"}` : null, rest ? `${rest} min` : null].filter(Boolean).join(" ");
}
/** An explicit whole-schedule move keeps custom calendar offsets and seconds.
 * A DST occurrence belongs to its original date and must be chosen again. */
export function moveSchedule(schedule: ScheduleV1, targetDate: string): ScheduleV1 {
  if (!schedule.eventStart.date) throw new Error("Choose an event start date first");
  const days = Temporal.PlainDate.from(targetDate).since(schedule.eventStart.date, { largestUnit: "day" }).days;
  const shift = (date: string | null) => date ? Temporal.PlainDate.from(date).add({ days }).toString() : null;
  const point = (value: LocalPoint): LocalPoint => ({ ...value, date: shift(value.date), ...(days ? { offset: null } : {}) });
  const reservation = schedule.reservation;
  return { ...schedule, eventStart: point(schedule.eventStart), eventEnd: point(schedule.eventEnd),
    accessStart: schedule.accessStart.mode === "custom" ? { mode: "custom", point: point(schedule.accessStart.point) } : schedule.accessStart,
    accessEnd: schedule.accessEnd.mode === "custom" ? { mode: "custom", point: point(schedule.accessEnd.point) } : schedule.accessEnd,
    reservation: reservation.mode === "timed" ? reservation : reservation.mode === "all_day"
      ? { ...reservation, startDate: shift(reservation.startDate)!, endDate: shift(reservation.endDate)! }
      : { ...reservation, startDate: shift(reservation.startDate), endDate: shift(reservation.endDate) } };
}
export function pointOccurrences(point: LocalPoint, timezone: string): { value: string; label: string }[] {
  if (!point.date || !point.time) return [];
  try {
    const local = Temporal.PlainDateTime.from(`${point.date}T${point.time}`);
    const earlier = local.toZonedDateTime(timezone, { disambiguation: "earlier" }), later = local.toZonedDateTime(timezone, { disambiguation: "later" });
    if (!earlier.toPlainDateTime().equals(local) || !later.toPlainDateTime().equals(local) || earlier.epochNanoseconds === later.epochNanoseconds) return [];
    return [{ value: earlier.offset, label: `First occurrence (UTC${earlier.offset})` }, { value: later.offset, label: `Second occurrence (UTC${later.offset})` }];
  } catch { return []; }
}
export function accessCaption(schedule: unknown): string {
  const resolved = resolveSchedule(schedule);
  if (resolved.reservationState === "legacy_unresolved") return "Legacy reserved dates — review access";
  if (resolved.reservationState === "all_day") return "Explicit full-day reservation";
  return `Setup ${durationLabel(resolved.durations.setupMinutes)} · Event ${durationLabel(resolved.durations.eventMinutes)} · Cleanup ${durationLabel(resolved.durations.cleanupMinutes)}`;
}
